import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	Box,
	Title,
	Text,
	Table,
	Button,
	Paper,
	Space,
	Modal,
	Group,
} from '@mantine/core'
import {
	getExperiencePositions,
	deleteStaffPosition,
} from '../services/actions/experienceActions'
import { getOrgUsers } from '../services/actions/orgActions'

const ManageStaffPage = () => {
	const { experienceSlug } = useParams()
	const navigate = useNavigate()

	const [staffUsers, setStaffUsers] = useState([])
	const [unassignedUsers, setUnassignedUsers] = useState([])
	const currentOrg = useSelector((state) => state.user.currentOrg)

	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
	const [selectedPosition, setSelectedPosition] = useState(null)

	const [error, setError] = useState('')

	useEffect(() => {
		const fetchData = async () => {
			try {
				const [users, positions] = await Promise.all([
					getOrgUsers(currentOrg.orgSlug),
					getExperiencePositions(experienceSlug),
				])

				const positionMap = Object.fromEntries(
					positions.map((p) => [p.userId, p])
				)

				const assigned = []
				const unassigned = []

				users.forEach((user) => {
					const position = positionMap[user.id]
					if (position) {
						assigned.push({ ...user, position })
					} else {
						unassigned.push(user)
					}
				})

				setStaffUsers(assigned)
				setUnassignedUsers(unassigned)
			} catch (err) {
				console.error('Failed to fetch staff data:', err)
				setError('Failed to load staff data.')
			}
		}

		if (currentOrg?.orgSlug) {
			fetchData()
		}
	}, [experienceSlug, currentOrg?.orgSlug])

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='sm'>
				Manage Staff {currentOrg?.name && `– ${currentOrg.name}`}
			</Title>

			{error && (
				<Text color='red' mb='md'>
					{error}
				</Text>
			)}

			{/* Assigned Staff */}
			<Title order={4} mt='md' mb='xs'>
				Assigned Staff
			</Title>

			{staffUsers.length > 0 ? (
				<Paper withBorder p='md' radius='md'>
					<Table striped highlightOnHover>
						<thead>
							<tr>
								<th>Name</th>
								<th>Email</th>
								<th>Level</th>
								<th>Position</th>
								<th>Station</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{staffUsers.map((user) => (
								<tr key={user.id}>
									<td>{user.name}</td>
									<td>{user.email}</td>
									<td>
										{user.position?.level === 'owner'
											? 'Owner'
											: user.position?.level === 'staff'
											? 'Staff'
											: '-'}
									</td>
									<td>{user.position?.positionName || '-'}</td>
									<td>{user.position?.stationName || '-'}</td>
									<td>
										<Button
											size='xs'
											variant='filled'
											onClick={() =>
												navigate(
													`/experiences/${experienceSlug}/manage/staff/${user.id}/edit`
												)
											}
										>
											Edit
										</Button>
										<Button
											size='xs'
											variant='light'
											color='red'
											onClick={() => {
												setSelectedPosition({
													id: user.position.id,
													name: user.name,
												})
												setConfirmDeleteOpen(true)
											}}
										>
											Remove
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</Paper>
			) : (
				<Text>No staff assigned yet.</Text>
			)}

			{/* Unassigned Users */}
			<Title order={4} mt='xl' mb='xs'>
				Available Users to Add
			</Title>

			{unassignedUsers.length > 0 ? (
				<Paper withBorder p='md' radius='md'>
					<Table striped highlightOnHover>
						<thead>
							<tr>
								<th>Name</th>
								<th>Email</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{unassignedUsers.map((user) => (
								<tr key={user.id}>
									<td>{user.name}</td>
									<td>{user.email}</td>
									<td>
										<Button
											size='xs'
											variant='filled'
											onClick={() =>
												navigate(
													`/experiences/${experienceSlug}/manage/staff/${user.id}/edit`
												)
											}
										>
											Add
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</Paper>
			) : (
				<Text>All users are assigned already.</Text>
			)}

			<Modal
				opened={confirmDeleteOpen}
				onClose={() => setConfirmDeleteOpen(false)}
				title='Confirm Removal'
				centered
			>
				<Text mb='md'>
					Are you sure you want to remove{' '}
					<strong>{selectedPosition?.name}</strong> from this position?
				</Text>
				<Group position='right'>
					<Button variant='light' onClick={() => setConfirmDeleteOpen(false)}>
						Cancel
					</Button>
					<Button
						color='red'
						onClick={async () => {
							try {
								await deleteStaffPosition(selectedPosition.id)
								setConfirmDeleteOpen(false)
								setSelectedPosition(null)
								// Refresh the list
								const [users, positions] = await Promise.all([
									getOrgUsers(currentOrg.orgSlug),
									getExperiencePositions(experienceSlug),
								])
								const positionMap = Object.fromEntries(
									positions.map((p) => [p.userId, p])
								)

								const assigned = []
								const unassigned = []

								users.forEach((user) => {
									const position = positionMap[user.id]
									if (position) {
										assigned.push({ ...user, position })
									} else {
										unassigned.push(user)
									}
								})

								setStaffUsers(assigned)
								setUnassignedUsers(unassigned)
							} catch (err) {
								console.error('Failed to delete position:', err)
								setError('Failed to remove staff from position.')
							}
						}}
					>
						Yes, Remove
					</Button>
				</Group>
			</Modal>
		</Box>
	)
}

export default ManageStaffPage
